import { Link } from 'react-router-dom';
import { TrendingUp, ShieldCheck, Users, Box, ArrowRight, CheckCircle2, ShoppingCart, Zap, Award, Clock, Star, ChevronLeft, ChevronRight, Quote, Phone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// 核心优势数据
const FEATURES = [
  {
    title: "资源整合",
    desc: "链接供需双方，输出优质资源，推动产业链高效整合。",
    icon: <Box className="w-6 h-6" />,
    color: "bg-blue-50 text-blue-600 group-hover:bg-[#1c5bff] group-hover:text-white",
    stats: { value: "500+", label: "合作企业" }
  },
  {
    title: "降本增效",
    desc: "通过数字化技术整合需求，助力行业伙伴显著降低采购成本。",
    icon: <TrendingUp className="w-6 h-6" />,
    color: "bg-green-50 text-green-600 group-hover:bg-[#1c5bff] group-hover:text-white",
    stats: { value: "30%", label: "降低成本" }
  },
  {
    title: "规范标准",
    desc: "建立统一的采购标准与规范，确保交易透明、合规、高效。",
    icon: <ShieldCheck className="w-6 h-6" />,
    color: "bg-purple-50 text-purple-600 group-hover:bg-[#1c5bff] group-hover:text-white",
    stats: { value: "100%", label: "合规保障" }
  },
  {
    title: "协同联动",
    desc: "打破信息孤岛，实现上下游企业间的无缝协同与联动。",
    icon: <Users className="w-6 h-6" />,
    color: "bg-orange-50 text-orange-600 group-hover:bg-[#1c5bff] group-hover:text-white",
    stats: { value: "24h", label: "实时响应" }
  }
];

// 服务数据
const SERVICES = [
  {
    id: 'products',
    icon: ShoppingCart,
    title: '优质商品',
    description: '精选工业材料，钢材、铝材、不锈钢等一站式采购',
    features: ['品质保证', '品类齐全', '源头直供'],
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/service1.png'
  },
  {
    id: 'services',
    icon: Zap,
    title: '极速交付',
    description: '高效物流体系，确保订单快速安全送达',
    features: ['当日发货', '全程跟踪', '安全可靠'],
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/service2.png'
  },
  {
    id: 'support',
    icon: Award,
    title: '品质保障',
    description: '严格品控体系，提供完善的售后服务支持',
    features: ['质量检测', '售后无忧', '专业客服'],
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/service3.png'
  }
];

export default function ShoppingCards() {
  // 动画相关状态
  const featureRef = useRef<HTMLElement>(null);
  const [featureVisible, setFeatureVisible] = useState(false);
  
  const serviceRef = useRef<HTMLElement>(null);
  const [serviceVisible, setServiceVisible] = useState(false);
  
  const statsRef = useRef<HTMLElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  
  const testimonialRef = useRef<HTMLElement>(null);
  const [testimonialVisible, setTestimonialVisible] = useState(false);
  
  const ctaRef = useRef<HTMLElement>(null);
  const [ctaVisible, setCtaVisible] = useState(false);

  useEffect(() => {
    const observers = [
      { ref: featureRef, setter: setFeatureVisible },
      { ref: serviceRef, setter: setServiceVisible },
      { ref: statsRef, setter: setStatsVisible },
      { ref: testimonialRef, setter: setTestimonialVisible },
      { ref: ctaRef, setter: setCtaVisible }
    ];

    observers.forEach(({ ref, setter }) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setter(true);
            observer.disconnect();
          }
        },
        { threshold: 0.2 }
      );

      if (ref.current) {
        observer.observe(ref.current);
      }
    });
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* 1. Hero 推广横幅区域 - 优化版 */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50">
        {/* 背景装饰 */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#1c5bff]/5 rounded-full blur-3xl animate-blob" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-purple-100 rounded-full blur-3xl animate-blob animation-delay-2000" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-50 rounded-full blur-3xl opacity-40 animate-blob animation-delay-4000" />
        </div>

        {/* 装饰圆环 */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 border border-gray-100 rounded-full" />
        <div className="absolute bottom-1/4 left-1/4 w-48 h-48 border border-gray-100 rounded-full" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* 左侧文字内容 */}
            <div className="space-y-8">
              {/* 标签 */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1c5bff]/10 text-[#1c5bff] rounded-full text-sm font-medium">
                <Zap className="w-4 h-4" />
                数字化采购平台
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                孔明在线商城
                <br />
                <span className="text-[#1c5bff] text-3xl">赋能产业升级</span>
              </h1>

              <p className="text-gray-600 text-lg leading-relaxed max-w-xl">
                倾力打造物资集采数字化平台！覆盖多领域多场景采购，以数字化技术整合需求，
                链接供需、输出规范标准与优质资源，推动产业链资源高效整合与协同联动，
                助力行业伙伴降本增效。
              </p>

              {/* 特性标签 */}
              <div className="flex flex-wrap gap-3">
                {['资源整合', '降本增效', '规范标准', '协同联动'].map((tag, index) => (
                  <div key={index} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#1c5bff]" />
                    {tag}
                  </div>
                ))}
              </div>

              {/* 按钮组 */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/shop" className="group bg-[#1c5bff] text-white font-bold py-4 px-8 rounded-full hover:bg-[#1c5bff]/90 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center gap-2">
                  立即探索
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/about" className="group border-2 border-gray-200 text-gray-700 font-bold py-4 px-8 rounded-full hover:border-[#1c5bff] hover:text-[#1c5bff] transition-all duration-300 flex items-center justify-center gap-2">
                  了解更多
                </Link>
              </div>

              {/* 信任指标 */}
              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                        {i}
                      </div>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">500+ 企业信赖</span>
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="text-sm text-gray-500 ml-1">4.9分</span>
                </div>
              </div>
            </div>

            {/* 右侧图片展示 */}
            <div className="relative hidden lg:block">
              <div className="relative">
                {/* 主图 */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/shop.png"
                    alt="数字化采购"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                {/* 浮动卡片1 */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">30%</p>
                    <p className="text-sm text-gray-500">成本降低</p>
                  </div>
                </div>

                {/* 浮动卡片2 */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">24h</p>
                    <p className="text-sm text-gray-500">快速响应</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部波浪装饰 */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L48 110C96 100 192 80 288 70C384 60 480 60 576 65C672 70 768 80 864 85C960 90 1056 90 1152 85C1248 80 1344 70 1392 65L1440 60V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* 2. 核心优势区域 - 优化版 */}
      <section ref={featureRef} className="py-24 bg-white relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-50 rounded-full blur-3xl opacity-60" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-50 rounded-full blur-3xl opacity-60" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* 区域标题 */}
          <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${featureVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block px-4 py-1.5 bg-[#1c5bff]/10 text-[#1c5bff] rounded-full text-sm font-medium mb-4">
              核心能力
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">四大核心能力，赋能数字化转型</h2>
            <p className="text-gray-500 text-lg">我们不仅仅是一个商城，更是您值得信赖的供应链合作伙伴</p>
          </div>

          {/* 卡片网格 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {FEATURES.map((feature, index) => (
              <div 
                key={index} 
                className={`group relative bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-[#1c5bff]/30 flex flex-col h-full ${featureVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* 悬停时的顶部彩色条 */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent group-hover:via-[#1c5bff] transition-all duration-500 rounded-t-2xl opacity-0 group-hover:opacity-100" />

                {/* 图标容器 */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 ${feature.color}`}>
                  {feature.icon}
                </div>

                {/* 文本内容 */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1c5bff] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-500 leading-relaxed mb-6 flex-grow">
                  {feature.desc}
                </p>

                {/* 数据指标展示 */}
                <div className="pt-6 border-t border-gray-50 mt-auto">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900 group-hover:text-[#1c5bff] transition-colors">
                      {feature.stats.value}
                    </span>
                    <span className="text-sm text-gray-500">
                      {feature.stats.label}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. 服务介绍区域 */}
      {/* <section ref={serviceRef} className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${serviceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block px-4 py-1.5 bg-[#1c5bff]/10 text-[#1c5bff] rounded-full text-sm font-medium mb-4">
              服务保障
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">一站式采购服务，全程无忧</h2>
            <p className="text-gray-500 text-lg">从商品选购到售后保障，我们为您提供全方位的服务支持</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((service, index) => (
              <div 
                key={service.id}
                className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 ${serviceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4 w-12 h-12 bg-[#1c5bff] rounded-xl flex items-center justify-center shadow-lg z-20 transition-transform duration-300 group-hover:scale-110">
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1c5bff] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="w-1.5 h-1.5 bg-[#1c5bff] rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {index == 0 && (<Link to="/shop" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 group/link hover:text-[#1c5bff] transition-colors">
                    了解更多
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                  </Link>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}
      {/* 自定义动画样式 */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .btn-primary {
          @apply px-6 py-3 rounded-lg bg-[#1c5bff] text-white font-semibold hover:bg-[#1c5bff]/90 transition-all duration-300;
        }
      `}</style>
    </div>
  );
}